import { AfterViewInit, Component, ViewChild } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { TranslateService } from '@ngx-translate/core';
import { SubMenuItem } from '@shared/components/header-sub-menu/header-sub-menu.component';
import { DocsService, IDbTemplateItem } from '@shared/services/docs.service';
import { CollectionDisplayStatus, getCollectionDisplayStatus, getCollectionJobCounts, ICollectionJobCounts, IPrinter, IPrintJob, IPrintJobCollection, PrintService, truncatePrintError } from '@shared/services/print.service';
import { catchError, forkJoin, of } from 'rxjs';

interface IJobCountPart {
	text: string;
	statusClass: string;
}

interface ITableCollectionViewModel {
	self: string;
	createdAt: number;
	printers: string;
	status: CollectionDisplayStatus;
	counts: ICollectionJobCounts;
	jobParts: IJobCountPart[];
	jobs: IPrintJob[];
}

@Component({
	selector: 'app-print-collection-jobs',
	templateUrl: './print-collection-jobs.component.html',
	styleUrl: './print-collection-jobs.component.scss',
	standalone: false,
})
export class PrintCollectionJobsComponent implements AfterViewInit {
	displayedColumns: string[] = ['expand', 'createdAt', 'printers', 'status', 'jobs'];
	dataSource = new MatTableDataSource<ITableCollectionViewModel>();
	expandedRow: ITableCollectionViewModel | null = null;
	isLoading = true;

	// Page-level aggregate counts (over the currently loaded collections only - the backend
	// has no server-side status filter for /collections, see fn-2 spec Edge Cases).
	statusCounts: Record<CollectionDisplayStatus, number> = { Working: 0, Done: 0, Failed: 0 };

	@ViewChild(MatPaginator) paginator: MatPaginator;
	@ViewChild(MatSort) sort: MatSort;

	private printerBySelf = new Map<string, IPrinter>();
	private templateBySelf = new Map<string, IDbTemplateItem>();

	favoriteItems: SubMenuItem[] = [
		{
			icon: () => 'acies_renew',
			text: () => 'print.refresh',
			click: () => this.updateList(),
		},
	];

	constructor(
		private printService: PrintService,
		private docsService: DocsService,
		private translateService: TranslateService,
	) {}

	ngAfterViewInit() {
		this.dataSource.paginator = this.paginator;
		this.dataSource.sort = this.sort;
		this.updateList();
	}

	// job.printer is the printer's self-ref (e.g. /print/printers/{id}); resolve it against the
	// printers list for a human-readable "(alias) description" label, falling back to the raw ref
	// when the printer can't be found (e.g. deleted since the job ran).
	getPrinterLabel(printerRef: string): string {
		const printer = this.printerBySelf.get(printerRef);
		return printer ? `(${printer.alias}) ${printer.description}` : printerRef;
	}

	// A job was created either with a template (docs template self-ref) or with raw urls, never
	// both - resolve whichever is present for display, falling back to the raw ref when the
	// template can't be found (e.g. deleted since the job ran).
	getJobTemplateOrUrls(job: IPrintJob): string {
		if (job.template) {
			const template = this.templateBySelf.get(job.template);
			return template ? template.name : job.template;
		}
		return job.urls?.join(', ') ?? '';
	}

	// Only the non-zero status counts are shown (e.g. an all-succeeded collection shows "4 done",
	// never "0 working · 0 failed · 4 done") - per the ask, a zero count says nothing at all.
	// Each part is colored the same as its matching Status chip (rowStatusClass).
	private getJobCountParts(counts: ICollectionJobCounts): IJobCountPart[] {
		const parts: IJobCountPart[] = [];
		if (counts.done > 0) {
			parts.push({
				text: this.translateService.instant('print.collection-jobs.jobs-count.done', {
					count: counts.done,
				}),
				statusClass: this.rowStatusClass('Done'),
			});
		}
		if (counts.working > 0) {
			parts.push({
				text: this.translateService.instant('print.collection-jobs.jobs-count.working', {
					count: counts.working,
				}),
				statusClass: this.rowStatusClass('Working'),
			});
		}
		if (counts.failed > 0) {
			parts.push({
				text: this.translateService.instant('print.collection-jobs.jobs-count.failed', {
					count: counts.failed,
				}),
				statusClass: this.rowStatusClass('Failed'),
			});
		}
		return parts;
	}

	private getTableViewModel(collection: IPrintJobCollection): ITableCollectionViewModel {
		const counts = getCollectionJobCounts(collection);
		return {
			self: collection.self,
			createdAt: collection.meta.createdAt,
			printers: [...new Set(collection.jobs.map((job) => this.getPrinterLabel(job.printer)))].join(', '),
			status: getCollectionDisplayStatus(collection),
			counts,
			jobParts: this.getJobCountParts(counts),
			jobs: collection.jobs,
		};
	}

	private updateStatusCounts(rows: ITableCollectionViewModel[]) {
		this.statusCounts = { Working: 0, Done: 0, Failed: 0 };
		rows.forEach((row) => this.statusCounts[row.status]++);
	}

	updateList() {
		this.isLoading = true;
		// Printer/template lookups are display-only enrichment (see getPrinterLabel /
		// getJobTemplateOrUrls) - a user with print:LabelListGet but not printer/template read
		// access must still see their collections, so those two calls fail soft into an empty
		// list rather than taking the whole forkJoin down with them.
		forkJoin({
			printers: this.printService.getPrinters().pipe(catchError(() => of<IPrinter[]>([]))),
			templates: this.docsService.getTemplateList().pipe(catchError(() => of<{ result: IDbTemplateItem[] }>({ result: [] }))),
			collections: this.printService.getCollections(),
		}).subscribe({
			next: ({ printers, templates, collections }) => {
				this.printerBySelf = new Map(printers.map((printer) => [printer.self, printer]));
				this.templateBySelf = new Map(templates.result.map((template) => [template.self, template]));
				const rows = collections.items.map((item) => this.getTableViewModel(item));
				this.dataSource.data = rows;
				this.updateStatusCounts(rows);
				this.onSort(this.sort);
				this.isLoading = false;
			},
			// Unlike the printer/template enrichment above, a failed getCollections() call has no
			// soft fallback - the primary data is simply unavailable. Clear the table/counters rather
			// than leaving stale rows on screen, and always drop isLoading so refresh is retryable.
			error: () => {
				this.dataSource.data = [];
				this.updateStatusCounts([]);
				this.isLoading = false;
			},
		});
	}

	applyFilter(event: KeyboardEvent) {
		if (event.target instanceof HTMLInputElement) {
			const filterValue = event.target.value;
			this.dataSource.filter = filterValue.trim().toLowerCase();
		}
	}

	public onSort(sort: Sort) {
		if (sort.direction === '') {
			this.sort.sort({ id: 'createdAt', start: 'desc', disableClear: true });
		}
	}

	toggleRow(row: ITableCollectionViewModel) {
		this.expandedRow = this.expandedRow === row ? null : row;
	}

	jobStatusClass(status: string): string {
		switch (status) {
			case 'Failed':
				return 'print-job-status--failed';
			case 'Succeeded':
				return 'print-job-status--done';
			default:
				return 'print-job-status--working';
		}
	}

	rowStatusClass(status: CollectionDisplayStatus): string {
		return `print-job-status--${status.toLowerCase()}`;
	}

	truncateError(message: string | undefined): string {
		return truncatePrintError(message);
	}
}
